import { Injectable } from '@angular/core';

/**
 * Cloudflare Turnstile site key (public). Turnstile's api.js does NOT embed the key
 * in its URL — it is passed to `turnstile.render()`, so this is the only place the
 * site key lives.
 */
export const TURNSTILE_SITE_KEY = '0x4AAAAAAETiN3n9ycRNhjNX';

/** Resolve to null if the challenge does not settle within this window (fail-open). */
const TURNSTILE_TIMEOUT_MS = 10000;

interface TurnstileRenderOptions {
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  sitekey: string;
  'timeout-callback'?: () => void;
}

interface TurnstileApi {
  remove(widgetId: string): void;
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Runs an invisible Cloudflare Turnstile challenge on demand and returns a single-use token
 * for bot-protecting OTP requests.
 *
 * Fails open — resolves `null` when the script is unavailable, the challenge errors, or it times
 * out. The server only verifies Turnstile when a token is present (and `disableTurnstile` is off),
 * so a client-side failure never blocks login.
 */
@Injectable({ providedIn: 'root' })
export class TurnstileService {
  execute(action: string): Promise<string | null> {
    return new Promise((resolve: (token: string | null) => void): void => {
      const turnstile: TurnstileApi | undefined = window.turnstile;
      if (!turnstile) {
        resolve(null);
        return;
      }

      const container: HTMLElement = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      document.body.appendChild(container);

      let settled: boolean = false;
      let widgetId: string | undefined;
      let timer: ReturnType<typeof setTimeout>;

      const finish = (token: string | null): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try {
          if (widgetId !== undefined) turnstile.remove(widgetId);
        } catch {
          // widget already removed — nothing to clean up.
        }
        container.remove();
        resolve(token);
      };

      timer = setTimeout((): void => finish(null), TURNSTILE_TIMEOUT_MS);

      try {
        widgetId = turnstile.render(container, {
          action,
          appearance: 'interaction-only',
          callback: (token: string): void => finish(token),
          'error-callback': (): void => finish(null),
          sitekey: TURNSTILE_SITE_KEY,
          'timeout-callback': (): void => finish(null),
        });
      } catch {
        finish(null);
      }
    });
  }
}
